import React, {
    FC,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ApolloProvider } from '@apollo/client';
import { AuthenticationContext } from '@/components/provider/AuthenticationProvider';
import Basement from '@/components/edifice/Basement/Basement';
import { createApolloClient } from '@/lib/apolloClient/createApolloClient';
import { apolloClientRef } from '@/lib/apolloClient/apolloClientRef';
import { getInitialApolloState } from '@/lib/apolloClient/getInitialApolloState';
import {
    getOAuth2Store,
    setOAuth2Store,
    removeOAuth2Store,
    API_BASE_URI,
    OAUTH2_CLIENT_ID,
    OAUTH2_CLIENT_SECRET,
} from '@/store';

const Shell: FC = () => {
    const [accessToken, setAccessToken] = useContext(AuthenticationContext);
    useEffect(() => {
        apolloClientRef.accessToken = accessToken;
    }, [accessToken]);

    const shouldHydrateOnBoot = useMemo(() => {
        const storedAuth = getOAuth2Store();
        return !storedAuth?.accessToken;
    }, []);

    const [client, setClient] = useState(() => {
        const initialState = getInitialApolloState(shouldHydrateOnBoot);
        const newClient = createApolloClient(
            setAccessToken,
            initialState,
            shouldHydrateOnBoot,
        );
        apolloClientRef.currentInstance = newClient;
        return newClient;
    });

    const prevAuthRef = useRef<boolean>(Boolean(accessToken));
    useEffect(() => {
        const isAuthed = Boolean(accessToken);
        const wasAuthed = prevAuthRef.current;
        if (isAuthed === wasAuthed) return;

        prevAuthRef.current = isAuthed;
        apolloClientRef.accessToken = accessToken;

        const nextClient = createApolloClient(setAccessToken, undefined, false);
        apolloClientRef.currentInstance = nextClient;

        setClient(nextClient);
    }, [accessToken, setAccessToken]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            (window as any).__PRERENDER_INJECTED
        )
            return;
        const interval = setInterval(async () => {
            const oAuth2Object = getOAuth2Store();

            if (!oAuth2Object?.accessToken || !oAuth2Object?.expires) return;

            if (oAuth2Object.expires - Date.now() < 300 * 1000) {
                try {
                    const res = await fetch(`${API_BASE_URI}/oauth/token`, {
                        method: 'POST',
                        body: new URLSearchParams({
                            grant_type: 'refresh_token',
                            refresh_token: oAuth2Object.refreshToken,
                            client_id: OAUTH2_CLIENT_ID,
                            client_secret: OAUTH2_CLIENT_SECRET,
                        }),
                    });

                    if (!res.ok) {
                        // Refresh token rejected by Drupal (expired or revoked)
                        removeOAuth2Store();
                        apolloClientRef.accessToken = null;
                        setAccessToken(null);
                        return;
                    }

                    const data = await res.json();

                    if (data.access_token) {
                        apolloClientRef.accessToken = data.access_token;
                        setAccessToken(data.access_token);

                        setOAuth2Store(
                            data.access_token,
                            data.refresh_token,
                            data.expires_in,
                            data.token_type,
                        );
                    }
                } catch (err) {
                    console.warn(
                        '[Auth] Sitzung abgelaufen, wechsle in den anonymen Modus.',
                    );
                    removeOAuth2Store();
                    apolloClientRef.accessToken = null;
                    setAccessToken(null);
                }
            }
        }, 60_000);

        return () => clearInterval(interval);
    }, [setAccessToken]);

    return (
        <ApolloProvider client={client} key={accessToken ? 'authed' : 'anon'}>
            <Basement />
        </ApolloProvider>
    );
};

export default Shell;
